import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import Banner from '../../atoms/banner/Banner'
import Text from '../../atoms/text/Text'
import LoadingSpinner from '../../atoms/loadingspinner/LoadingSpinner'
import Container from '../../atoms/page/Page'
import VerticalGroup from '../../atoms/verticalgroup/VerticalGroup'
import ModelConfiguration from './ModelConfiguration/ModelConfiguration'
import ModelDecisions from './ModelDecisions/ModelDecisions'
import ModelMarketShare from './ModelMarketShare/ModelMarketShare'
import ModelTreasuryCalculations from './ModelTreasuryCalculations/ModelTreasuryCalculations'
import ModelVarianceChecks from './ModelVarianceChecks/ModelVarianceChecks'
import ModelSetupChecks from './ModelSetupChecks/ModelSetupChecks'
import ModelTeamResults from './ModelTeamResults/ModelTeamResults'
import ModelKeyResults from './ModelKeyResults/ModelKeyResults'
import ModelAPI from '../../../services/modelApi'
import { useMemoRequest } from '../../../hooks/useMemoRequest'
import InlineGroup from '../../atoms/inlinegroup/InlineGroup'
import Ribbon from '../../atoms/ribbon/Ribbon'
import api from '../../../services/api'
import IntegerDropdown from '../../organisms/integer-dropdown/IntegerDropdown'
import getTeamName from './utils/getTeamName'
import { ModelContext, useModelContextValue } from './context'
import { reducer } from './state'
import useQuery from '../../../hooks/useQuery'

type Tab = 'configuration' | 'decisions' | 'wsf' | 'marketshare' | 'results' | 'key_results' |'variancecheck' | 'setupcheck'

interface GetConfigurationAndStatusAndEventResponse {
  configuration: ModelAPI.ConfigurationResponse
  status: API.GameStatus
  event: API.EventResponse
}

export const SecondaryPageHeader = (): JSX.Element => {
  const query = useQuery()
  const eventTitle = query.get('eventTitle')
  const clientName = query.get('clientName')
  if (!clientName || !eventTitle) {
    return <span></span>
  }
  return <InlineGroup spaceLeft={2}>
    <Text colour='green' size='sm'>{clientName}</Text>
    <Text size='sm'>—</Text>
    <Text size='sm'>{eventTitle}</Text>
  </InlineGroup>
}

const Model: React.FC = () => {
  let { eventId } = useParams<{ eventId: string }>()
  const query = useQuery()
  let initialTeam = 1
  const queryTeam = query.get('team')
  if (queryTeam != null) {
    initialTeam = parseInt(queryTeam)
  }
  let initialTab: Tab = 'configuration'
  const queryTab = query.get('tab')
  if (queryTab != null) {
    initialTab = queryTab as Tab
  }
  const [team, setTeam] = useState(initialTeam)
  const [tab, setTab] = useState<Tab>(initialTab)
  const history = useHistory()

  useEffect(() => {
    if (queryTab !=null) {
      setTab(queryTab as Tab)
    }

    if (queryTeam != null) {
      const parsedQueryTeam = parseInt(queryTeam)
      setTeam(parsedQueryTeam)
    }
  }, [queryTab, queryTeam])

  const [state, dispatch] = useReducer(reducer, {
    config: undefined,
    selectedRound: 0
  })

  const getConfigurationAndStatusAndEvent = useCallback(async () => {
    const configuration = await ModelAPI.getModelConfiguration(eventId)
    const status = await api.getGameStatus(eventId)
    const event = await api.getEvent(eventId)
    dispatch({
      type: 'UpdateConfig',
      payload: configuration
    })
    dispatch({
      type: 'UpdateSelectedRound',
      payload: configuration.currentRound
    })
    dispatch({
      type: 'UpdateGameStatus',
      payload: status
    })
    return { configuration, status, event }
  }, [eventId])

  let { inProgress, data, error, refresh } = useMemoRequest<GetConfigurationAndStatusAndEventResponse>(getConfigurationAndStatusAndEvent)

  useEffect(() => {
    const eventTitle = data?.event ? `${data?.event.name} ${data?.event.streamName ? `(${data?.event.streamName})` : ''}` : ''
    const clientName = data?.event?.client ?? ''
    if (query.get('eventTitle') !== eventTitle || query.get('clientName') !== clientName) {
      query.set('eventTitle', eventTitle)
      query.set('clientName', clientName)
      history.push({ search: query.toString() })
    }
  }, [query, data?.event, history])

  const { numberOfTeams, totalNumberOfTeams, numberOfRounds } = useMemo(() => {
    if (!data) {
      return { numberOfTeams: 0, totalNumberOfTeams: 0, numberOfRounds: 0 }
    }
    const { numberOfTeams, numberOfCpuTeams, numberOfRounds } = data.configuration
    const totalNumberOfTeams = numberOfTeams + numberOfCpuTeams
    return { numberOfTeams, totalNumberOfTeams, numberOfRounds }
  }, [data])

  const heading = useMemo(() => {
    switch (tab) {
      case 'configuration': return 'Configuration'
      case 'decisions': return 'Player Decisions'
      case 'marketshare': return 'Market Calculations'
      case 'wsf': return 'Treasury Calculations'
      case 'results': return 'Core Calculations'
      case 'key_results': return 'Key Results'
      case 'variancecheck': return 'Checks'
      case 'setupcheck': return `Checks - Data Displayed To Users During Round ${state.selectedRound}`
    }
  }, [tab, state.selectedRound])

  const modelContext = useModelContextValue(state, dispatch)

  return (
    <ModelContext.Provider value={modelContext}>
      <Container>
        <InlineGroup spaceLeft={2} className='h-16'>
          <Ribbon first active={tab === 'configuration'} title='Configuration' icon='settings' onClick={() => setTab('configuration')} />
          <Ribbon active={tab === 'decisions'} title='Decisions' icon='tick' onClick={() => setTab('decisions')} />
          <Ribbon active={tab === 'marketshare'} title='Market' icon='shareHolderReturn' onClick={() => setTab('marketshare')} />
          <Ribbon active={tab === 'wsf'} title='Treasury Calculations' icon='treasure' onClick={() => setTab('wsf')} />
          <Ribbon active={tab === 'results'} title='Core Calculations' icon='calculator' onClick={() => setTab('results')} />
          <Ribbon active={tab === 'key_results'} title='Key Results' icon='key' onClick={() => setTab('key_results')} />
          <Ribbon active={tab === 'variancecheck'} title='Checks' icon='clipboard' onClick={() => setTab('variancecheck')} />
          <Ribbon last active={tab === 'setupcheck'} title='Setup Check' icon='clipboard' onClick={() => setTab('setupcheck')} />
        </InlineGroup >
        <InlineGroup verticalCenter spread block>
          <h2>{heading}</h2>
          <InlineGroup spread spaceLeft={2}>
            {numberOfTeams !== 0 && data != null && data.status && <IntegerDropdown small labelFormatter={(team: number) => getTeamName(team, numberOfTeams, data!.status.teams)} min={1} max={totalNumberOfTeams} onChange={(selected) => setTeam(selected.value)} value={team} />}
            {numberOfRounds !== 0 && data != null && <IntegerDropdown
              small
              labelFormatter={(round: number) => `Round ${round}`}
              min={0}
              max={numberOfRounds}
              onChange={(selected) => {
                dispatch({
                  type: 'UpdateSelectedRound',
                  payload: selected.value
                })
              }}
              value={state.selectedRound}
            />}
          </InlineGroup>
        </InlineGroup>
        <Banner type='error' active={!!error} message={error?.message ?? ''} />
        {inProgress && <LoadingSpinner />}
        <VerticalGroup full>
          {tab === 'configuration' && <ModelConfiguration configuration={data?.configuration} eventId={eventId} refreshConfiguration={refresh} />}
          {tab === 'decisions' && !!data && <ModelDecisions eventId={eventId} selectedRound={state.selectedRound} configuration={data.configuration} teams={data.status.teams} />}
          {tab === 'marketshare' && !!data && <ModelMarketShare eventId={eventId} selectedRound={state.selectedRound} configuration={data.configuration} teams={data.status.teams} />}
          {tab === 'wsf' && !!data && <ModelTreasuryCalculations eventId={eventId} selectedRound={state.selectedRound} configuration={data.configuration} selectedTeam={team}/>}
          {tab === 'results' && !!data && <ModelTeamResults eventId={eventId} selectedRound={state.selectedRound} configuration={data.configuration} selectedTeam={team}/>}
          {tab === 'key_results' && !!data && <ModelKeyResults eventId={eventId} selectedRound={state.selectedRound} configuration={data.configuration} selectedTeam={team}/>}
          {tab === 'variancecheck' && !!data && <ModelVarianceChecks eventId={eventId} selectedRound={state.selectedRound} configuration={data.configuration} selectedTeam={team}/>}
          {tab === 'setupcheck' && !!data && <ModelSetupChecks eventId={eventId} selectedRound={state.selectedRound} configuration={data.configuration} selectedTeam={team}/>}
        </VerticalGroup>
      </Container>
    </ModelContext.Provider>
  )
}

export default Model